import { DECISION_TYPE_KEY } from '@/features/work-items/useItemHref';
import { projectItemPath } from '@/features/work-items/projectItemUrls';
import { itemPagePath } from '@/features/work-items/fullPagePaths';
import { SELECTED_ITEM_AREA_PARAM, SELECTED_ITEM_PARAM } from '@/features/views/viewUrlState';
import { workAreaRouteRefOf } from '@/routing/workAreaRoute';
import type { IProject } from '@featit/shared';
import {
  isWorkAreaOverviewPath,
  pageOwnsItemSelection,
  workAreasDecisionPath,
} from './workAreaOverviewUrls';

/**
 * I-1304: the item's own page, for an address that names the desktop's item panel
 * (`allWorkAreasItemHref`). A phone never shows that panel. The panel's address carries no type,
 * and a decision's own page is a decision's address, so the type is the item's own, once read.
 */

/** The item an All work areas address opens in the panel, or `null` for any other address. */
export function panelItemOf(address: string): { workAreaId: string; itemKey: string } | null {
  const url = new URL(address, 'http://app.invalid');
  // The Views catalog and the Items list read the same parameters for a panel of their own.
  if (!isWorkAreaOverviewPath(url.pathname) || pageOwnsItemSelection(url.pathname)) {
    return null;
  }
  const itemKey = url.searchParams.get(SELECTED_ITEM_PARAM)?.trim();
  const workAreaId = url.searchParams.get(SELECTED_ITEM_AREA_PARAM)?.trim();
  return itemKey && workAreaId ? { workAreaId, itemKey } : null;
}

/**
 * Where an item of `typeKey` has its own page: a decision at a decision's address. `fullPage` is a
 * desktop's: the item's full page (`itemPagePath`), where a phone's item route is already a page
 * (I-1357).
 */
export function ownPageOf(
  workArea: Pick<IProject, 'slug' | 'namespace'> | null | undefined,
  itemKey: string,
  typeKey: string | null,
  { fullPage = false }: { fullPage?: boolean } = {},
): string {
  const ref = workAreaRouteRefOf(workArea);
  if (typeKey === DECISION_TYPE_KEY) return workAreasDecisionPath(ref, itemKey);
  return fullPage ? itemPagePath(ref, itemKey) : projectItemPath(ref, itemKey);
}

/** As much of an item as its own page's address needs. */
interface ITypedItem {
  readonly itemType?: { readonly key: string } | null;
}

interface IItemReader {
  getItemByKey(key: string, projectId?: string | null): ITypedItem | undefined;
  loadItem(projectId: string, key: string): Promise<ITypedItem | null>;
}

/**
 * The item's type, from the store when it is there, else read. `null` for an item that cannot be
 * read: it opens at the item route, which says why.
 */
export async function typeOfItem(
  items: IItemReader,
  workAreaId: string,
  itemKey: string,
): Promise<string | null> {
  const known = items.getItemByKey(itemKey, workAreaId)?.itemType?.key;
  if (known !== undefined) return known;
  try {
    return (await items.loadItem(workAreaId, itemKey))?.itemType?.key ?? null;
  } catch {
    return null;
  }
}
